import i18next, { type TOptions } from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import { englishMessages as en, turkishMessages as tr } from "@/shared/i18n/catalogues";

export type Locale = "en" | "tr";
export const LOCALE_STORAGE_KEY = "exognitos.locale";

/** Accept legacy regional values while keeping the account contract canonical. */
export function normalizeLocale(value: string | null | undefined): Locale {
  return value?.toLowerCase().startsWith("tr") ? "tr" : "en";
}

/** Browser language is deliberately not a preference: English is the default. */
export function readSavedLocale(): Locale {
  try {
    return normalizeLocale(localStorage.getItem(LOCALE_STORAGE_KEY));
  } catch {
    return "en";
  }
}

export const i18n = i18next.createInstance();
void i18n.use(initReactI18next).init({
  resources: { en: { translation: en }, tr: { translation: tr } },
  lng: readSavedLocale(),
  fallbackLng: "en",
  supportedLngs: ["en", "tr"],
  initAsync: false,
  keySeparator: false,
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

function applyLanguageMetadata() {
  if (typeof document !== "undefined") document.documentElement.lang = getLocale();
}
i18n.on("languageChanged", applyLanguageMetadata);
applyLanguageMetadata();

export function getLocale(): Locale {
  return i18n.resolvedLanguage === "tr" || i18n.language === "tr" ? "tr" : "en";
}

/** Display locale only; timezone, week start, values and API identifiers remain independent. */
export function getFormatLocale(): string {
  return getLocale() === "tr" ? "tr-TR" : "en-GB";
}

export function t(key: string, options?: TOptions): string {
  return i18n.t(key, options) as string;
}

export async function setLocale(locale: Locale): Promise<void> {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // A storage-denied browser can still use the chosen language for this session.
  }
  await i18n.changeLanguage(locale);
}

/**
 * A signed-in account is the source of truth across devices. Local storage is
 * still retained as the fast pre-login/default value for this browser.
 */
export async function syncAccountLocale(accountLocale: string): Promise<void> {
  const locale = normalizeLocale(accountLocale);
  if (getLocale() === locale && readSavedLocale() === locale) return;
  await setLocale(locale);
}

/** Subscribe the calling component without changing its identity or discarding local drafts. */
export function useLocale(): Locale {
  useTranslation(undefined, { i18n });
  return getLocale();
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(getFormatLocale(), options).format(value);
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === LOCALE_STORAGE_KEY) void i18n.changeLanguage(readSavedLocale());
  });
}
